import { useState, useEffect } from 'react';
import { adminAPI, dataAPI, poolAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Shield, Users, Trophy, Database, RefreshCw, DollarSign, Mail, BarChart3, Settings, Eye } from 'lucide-react';

export default function AdminPanel() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [allPools, setAllPools] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [unreadContacts, setUnreadContacts] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [poolSearch, setPoolSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  useEffect(() => { document.title = "Admin — Pools FC"; }, []);

  const filteredUsers = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    return true;
  });

  const filteredPools = allPools.filter(p => {
    if (poolSearch) {
      const s = poolSearch.toLowerCase();
      if (!p.name?.toLowerCase().includes(s) && !p.organizer_name?.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const planFilteredPools = allPools.filter(p => {
    if (planFilter === 'paid') return p.plan && p.plan !== 'free';
    if (planFilter === 'free') return !p.plan || p.plan === 'free';
    return true;
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [s, t, ss, c, p, st] = await Promise.all([
          adminAPI.stats(), dataAPI.getAllTeams(), adminAPI.syncStatus(), adminAPI.contacts(), adminAPI.pools(), adminAPI.getSettings(),
        ]);
        setStats(s.data);
        setTeams(t.data.teams);
        setSyncStatus(ss.data);
        setContacts(c.data.messages);
        setUnreadContacts(c.data.unread || 0);
        setAllPools(p.data.pools);
        setSettings(st.data.settings);
      } catch (err) { console.error(err); }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await adminAPI.users({ search: userSearch || undefined });
        setUsers(r.data.users);
      } catch (err) { console.error(err); }
    };
    load();
  }, [userSearch]);


  const loadUserDetail = async (userId) => {
    try {
      const res = await adminAPI.userDetail(userId);
      setUserDetail(res.data);
      setSelectedUser(userId);
    } catch (err) { toast.error('Failed to load user details'); }
  };

  const loadSettings = async () => {
    try { const res = await adminAPI.getSettings(); setSettings(res.data.settings); } catch (e) { console.error(e); }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      await adminAPI.updateSettings(settings);
      toast.success('Settings saved');
    } catch (err) { toast.error('Failed to save'); }
    finally { setSavingSettings(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await adminAPI.syncResults();
      toast.success(r.data.message);
      const ss = await adminAPI.syncStatus();
      setSyncStatus(ss.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleTierChange = async (teamId, newTier) => {
    try {
      await adminAPI.setTeamTier(teamId, parseInt(newTier));
      setTeams(prev => prev.map(t => t.id === teamId ? { ...t, tier: parseInt(newTier) } : t));
      toast.success('Tier updated');
    } catch (err) { toast.error('Failed'); }
  };

  if (user?.role !== 'admin') return <div className="text-center py-12"><p className="text-muted-foreground">Admin access required</p></div>;

  const tierGroups = [1,2,3,4,5].map(tier => ({ tier, teams: teams.filter(t => t.tier === tier) }));
  const unassigned = teams.filter(t => !t.tier || t.tier === 0);
  const multipliers = { 1: 'Win 2x, Draw 1x', 2: 'Win 4x, Draw 2x', 3: 'Win 6x, Draw 3x', 4: 'Win 8x, Draw 4x', 5: 'Win 10x, Draw 5x' };

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-6 flex items-center gap-2"><Shield className="h-6 w-6" />Admin Dashboard</h1>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Users</p><p className="font-heading text-xl font-bold">{stats.total_users || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Pools</p><p className="font-heading text-xl font-bold">{stats.total_pools || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Entries</p><p className="font-heading text-xl font-bold">{stats.total_entries || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Revenue</p><p className="font-heading text-xl font-bold text-[#5b3cc4]">{stats.total_revenue || 0}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Paid / Free</p><p className="font-heading text-xl font-bold">{stats.paid_plans || 0} / {stats.free_plans || 0}</p></CardContent></Card>
      </div>

      <Tabs defaultValue="sync">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="sync"><RefreshCw className="h-3.5 w-3.5 mr-1" />API Sync</TabsTrigger>
          <TabsTrigger value="tiers"><Database className="h-3.5 w-3.5 mr-1" />Tiers</TabsTrigger>
          <TabsTrigger value="pools"><Trophy className="h-3.5 w-3.5 mr-1" />Pools</TabsTrigger>
          <TabsTrigger value="plans"><DollarSign className="h-3.5 w-3.5 mr-1" />Plans</TabsTrigger>
          <TabsTrigger value="users"><Users className="h-3.5 w-3.5 mr-1" />Users</TabsTrigger>
          <TabsTrigger value="contacts" className="relative"><Mail className="h-3.5 w-3.5 mr-1" />Contacts
            {unreadContacts > 0 && <span className="absolute -top-1 -right-1 h-4 min-w-[16px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1">{unreadContacts}</span>}
          </TabsTrigger>
          <TabsTrigger value="settings" onClick={() => { if (!settings) loadSettings(); }}><Settings className="h-3.5 w-3.5 mr-1" />Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="sync">
          <div className="space-y-4">
            <Card>
              <CardHeader><CardTitle>API-Football Sync</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div className="rounded-lg bg-secondary/50 p-3"><p className="text-xs text-muted-foreground">Matches in DB</p><p className="font-heading text-lg font-bold">{syncStatus?.match_count || 0}</p></div>
                  <div className="rounded-lg bg-secondary/50 p-3"><p className="text-xs text-muted-foreground">Season Goals</p><p className="font-heading text-lg font-bold">{syncStatus?.season_goals || 0}</p></div>
                  <div className="rounded-lg bg-secondary/50 p-3"><p className="text-xs text-muted-foreground">API Plan</p><p className="font-heading text-sm font-bold">{syncStatus?.api_status?.subscription?.plan || 'N/A'}</p></div>
                  <div className="rounded-lg bg-secondary/50 p-3"><p className="text-xs text-muted-foreground">Last Sync</p><p className="text-xs font-medium">{syncStatus?.last_sync?.completed_at ? new Date(syncStatus.last_sync.completed_at).toLocaleString() : 'Never'}</p></div>
                  <div className="rounded-lg bg-[#f3f0ff] p-3 border border-[#5b3cc4]/20"><p className="text-xs text-[#5b3cc4] font-medium">Auto-Sync</p><p className="text-xs font-bold">{syncStatus?.auto_sync?.schedule || 'N/A'}</p></div>
                </div>
                <Button onClick={handleSync} disabled={syncing}><RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />{syncing ? 'Syncing...' : 'Sync Now'}</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tiers">
          <div className="space-y-4">
            {unassigned.length > 0 && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardHeader><CardTitle className="text-yellow-800">Unassigned Teams ({unassigned.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {unassigned.map(team => (
                      <div key={team.id} className="flex items-center gap-2 p-2 rounded-lg bg-white border">
                        <img src={team.logo} alt="" className="h-6 w-6 rounded" onError={(e) => { e.target.style.display='none'; }} />
                        <span className="text-sm flex-1">{team.name}</span>
                        <select className="text-xs border rounded px-1 py-0.5" value={team.tier || 0} onChange={(e) => handleTierChange(team.id, e.target.value)}>
                          <option value={0}>-</option>{[1,2,3,4,5].map(t => <option key={t} value={t}>T{t}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            {tierGroups.map(({ tier, teams: tTeams }) => (
              <Card key={tier}>
                <CardHeader><div className="flex items-center justify-between"><CardTitle>Tier {tier}</CardTitle><Badge variant="secondary">{multipliers[tier]}</Badge></div></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {tTeams.map(team => (
                      <div key={team.id} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/30">
                        <img src={team.logo} alt="" className="h-6 w-6 rounded" onError={(e) => { e.target.style.display='none'; }} />
                        <div className="flex-1">
                          <div className="text-sm font-medium">{team.name}</div>
                          <div className="text-[10px] text-muted-foreground">Rank #{team.suggested_rank}</div>
                        </div>
                        <select className="text-xs border rounded px-1 py-0.5" value={team.tier} onChange={(e) => handleTierChange(team.id, e.target.value)}>
                          {[1,2,3,4,5].map(t => <option key={t} value={t}>T{t}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* POOLS TAB */}
        <TabsContent value="pools">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center gap-3">
                <Input placeholder="Search by pool name or organizer..." value={poolSearch} onChange={(e) => setPoolSearch(e.target.value)} className="max-w-xs" />
                <Badge variant="secondary">{filteredPools.length} pools</Badge>
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" size="sm" className="text-yellow-700 border-yellow-300 hover:bg-yellow-50" onClick={async () => {
                    if (!window.confirm('Lock ALL open pools? This will prevent new entries across all pools.')) return;
                    try {
                      await adminAPI.lockAllPools();
                      toast.success('All pools locked');
                      const p = await adminAPI.pools();
                      setAllPools(p.data.pools);
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }}>Lock All Pools</Button>
                  <Button variant="outline" size="sm" onClick={async () => {
                    if (!window.confirm('Unlock ALL locked pools?')) return;
                    try {
                      await adminAPI.unlockAllPools();
                      toast.success('All pools unlocked');
                      const p = await adminAPI.pools();
                      setAllPools(p.data.pools);
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }}>Unlock All Pools</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ minWidth: "700px" }}>
                  <thead>
                    <tr className="border-b">
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">Pool Name</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">Organizer</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">PIN</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">Plan</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">Members</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">Entries</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">Status</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPools.map(p => (
                      <tr key={p.id} className="border-b hover:bg-secondary/30">
                        <td className="px-3 py-2.5 font-medium">{p.name}</td>
                        <td className="px-3 py-2.5 text-muted-foreground">{p.organizer_name}</td>
                        <td className="px-3 py-2.5 text-center font-mono font-bold text-[#5b3cc4]">{p.pin || '—'}</td>
                        <td className="px-3 py-2.5 text-center">
                          <select className="text-xs border rounded px-1.5 py-1 bg-[#f3f0ff] text-[#5b3cc4] font-medium" value={p.plan || 'free'} onChange={async (e) => {
                            try { await poolAPI.upgrade(p.id, e.target.value); toast.success(`Pool upgraded to ${e.target.value}`); const res = await adminAPI.pools(); setAllPools(res.data.pools); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                          }}>
                            <option value="free">Free</option><option value="basic">Basic</option><option value="starter">Starter</option><option value="standard">Standard</option><option value="pro">Pro</option>
                          </select>
                        </td>
                        <td className="px-3 py-2.5 text-center">{p.member_count || 0}</td>
                        <td className="px-3 py-2.5 text-center">{p.entry_count || 0}</td>
                        <td className="px-3 py-2.5 text-center"><Badge className={p.status === 'open' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-yellow-100 text-yellow-700 text-[10px]'}>{p.status}</Badge></td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button variant="outline" size="sm" className="text-xs h-7" asChild><a href={`/pools/${p.id}`}>View</a></Button>
                            <Button variant="outline" size="sm" className="text-xs h-7 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={async () => {
                              if (!window.confirm(`Delete "${p.name}" and all its entries, members, and messages? This cannot be undone.`)) return;
                              try { await adminAPI.deletePool(p.id); toast.success(`Pool "${p.name}" deleted`); setAllPools(prev => prev.filter(x => x.id !== p.id)); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
                            }}>Delete</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredPools.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-muted-foreground text-sm">No pools found</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PLANS TAB */}
        <TabsContent value="plans">
          <div className="space-y-4">
            <div className="flex gap-2 mb-2">
              {['all', 'paid', 'free'].map(f => (
                <Button key={f} variant={planFilter === f ? 'default' : 'outline'} size="sm"
                  className={planFilter === f ? 'bg-[#5b3cc4] hover:bg-[#4e33a8]' : ''}
                  onClick={() => setPlanFilter(f)}>
                  {f === 'all' ? 'All Plans' : f === 'paid' ? 'Paid Only' : 'Free Only'}
                </Button>
              ))}
              <Badge variant="secondary" className="ml-2">{planFilteredPools.length} pools</Badge>
            </div>

            <Card>
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ minWidth: "700px" }}>
                    <thead>
                      <tr className="border-b">
                        <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">Plan</th>
                        <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">Pool Name</th>
                        <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground">Organizer</th>
                        <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">Amount</th>
                        <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">Capacity</th>
                        <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">Members</th>
                        <th className="px-3 py-2.5 text-center text-xs font-semibold text-muted-foreground">Payment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {planFilteredPools.map(p => (
                        <tr key={p.id} className="border-b hover:bg-secondary/30">
                          <td className="px-3 py-2.5"><Badge className={p.plan && p.plan !== 'free' ? 'bg-[#f3f0ff] text-[#5b3cc4]' : ''} variant={!p.plan || p.plan === 'free' ? 'secondary' : 'default'}>{p.plan_label || 'Free'}</Badge></td>
                          <td className="px-3 py-2.5 font-medium">{p.name}</td>
                          <td className="px-3 py-2.5 text-muted-foreground">{p.organizer_name}</td>
                          <td className="px-3 py-2.5 text-center font-mono font-bold">{p.plan_price || 0}</td>
                          <td className="px-3 py-2.5 text-center">{p.plan_capacity || 8}</td>
                          <td className="px-3 py-2.5 text-center">{p.member_count || 0}</td>
                          <td className="px-3 py-2.5 text-center"><Badge className={p.plan_payment_status === 'mock_paid' || p.plan_payment_status === 'completed' ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-yellow-100 text-yellow-700 text-[10px]'}>{p.plan_payment_status === 'mock_paid' ? 'Mock Paid' : p.plan_payment_status || 'Free'}</Badge></td>
                        </tr>
                      ))}
                      {planFilteredPools.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground text-sm">No plans found</td></tr>}
                    </tbody>
                  </table>
                </div>

                {/* Revenue summary */}
                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Revenue (Paid Plans)</span>
                  <span className="font-heading text-xl font-bold text-[#5b3cc4]">{allPools.filter(p => p.plan && p.plan !== 'free').reduce((sum, p) => sum + (p.plan_price || 0), 0)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-center gap-3">
                  <Input placeholder="Search users..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="max-w-xs" />
                  <div className="flex gap-1">
                    {['all', 'entrant', 'organizer', 'admin'].map(r => (
                      <Button key={r} variant={roleFilter === r ? 'default' : 'outline'} size="sm"
                        className={roleFilter === r ? 'bg-[#5b3cc4] hover:bg-[#4e33a8]' : ''}
                        onClick={() => setRoleFilter(r)}>
                        {r === 'all' ? 'All' : r.charAt(0).toUpperCase() + r.slice(1)}
                      </Button>
                    ))}
                  </div>
                  <Badge variant="secondary">{filteredUsers.length} users</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {filteredUsers.map(u => (
                    <div key={u.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/30">
                      <div>
                        <span className="text-sm font-medium">{u.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">{u.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-[10px]">{u.role}</Badge>
                        {u.suspended && <Badge className="bg-red-100 text-red-700 text-[10px]">Suspended</Badge>}
                        <select className="text-xs border rounded px-1 py-0.5" value={u.role} onChange={async (e) => { await adminAPI.changeRole(u.id, e.target.value); toast.success('Role updated'); }}>
                          <option value="entrant">Entrant</option><option value="organizer">Organizer</option><option value="admin">Admin</option>
                        </select>
                        <Button variant="ghost" size="sm" className="h-6 text-xs text-[#5b3cc4]" onClick={() => loadUserDetail(u.id)}><Eye className="h-3 w-3 mr-1" />View</Button>
                        {u.role !== 'admin' && <Button variant="ghost" size="sm" className="h-6 text-xs text-destructive" onClick={async () => {
                          if (!window.confirm(`Delete "${u.name}" (${u.email}) and all their pools, entries, and messages? This cannot be undone.`)) return;
                          try { await adminAPI.deleteUser(u.id); toast.success(`User "${u.name}" deleted`); const r = await adminAPI.users({ search: userSearch || undefined }); setUsers(r.data.users); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
                        }}>Delete</Button>}
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No users found</p>}
                </div>
              </CardContent>
            </Card>

            {/* Email Organizers */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-[#5b3cc4]" />Email Organizers</CardTitle>
                <p className="text-xs text-muted-foreground">Send a message to all pool organizers. (SendGrid integration coming soon — messages saved for now.)</p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-medium">Subject</label>
                  <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="e.g., Season 2026/27 — Important Update" />
                </div>
                <div>
                  <label className="text-xs font-medium">Message</label>
                  <textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder="Write your message to all organizers..." className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y" />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{users.filter(u => u.role === 'organizer').length} organizer(s) will receive this message</p>
                  <Button className="bg-[#5b3cc4] hover:bg-[#4e33a8]" disabled={!emailSubject.trim() || !emailBody.trim()} onClick={() => { toast.success(`Message saved. Will be sent to ${users.filter(u => u.role === 'organizer').length} organizers when SendGrid is integrated.`); setEmailSubject(''); setEmailBody(''); }}>
                    <Mail className="h-3.5 w-3.5 mr-1" />Save Draft
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="contacts">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Contact Messages</CardTitle>
                {unreadContacts > 0 && <Badge className="bg-red-100 text-red-700">{unreadContacts} unread</Badge>}
              </div>
            </CardHeader>
            <CardContent>
              {contacts.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">No messages</p> :
                <div className="space-y-3">
                  {contacts.map(c => (
                    <div key={c.id} className={`border rounded-lg p-3 ${!c.read ? 'bg-[#f3f0ff]/30 border-[#5b3cc4]/20' : ''}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          {!c.read && <span className="h-2 w-2 rounded-full bg-[#5b3cc4] flex-shrink-0" />}
                          <span className={`text-sm ${!c.read ? 'font-bold' : 'font-medium'}`}>{c.name} &lt;{c.email}&gt;</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                          <button onClick={async () => {
                            const newRead = !c.read;
                            await adminAPI.toggleContactRead(c.id, newRead);
                            setContacts(prev => prev.map(m => m.id === c.id ? { ...m, read: newRead } : m));
                            setUnreadContacts(prev => newRead ? prev - 1 : prev + 1);
                          }} className={`text-[10px] px-2 py-0.5 rounded-full ${c.read ? 'bg-secondary text-muted-foreground hover:bg-secondary/80' : 'bg-[#5b3cc4] text-white hover:bg-[#4e33a8]'}`}>
                            {c.read ? 'Mark unread' : 'Mark read'}
                          </button>
                        </div>
                      </div>
                      <p className={`text-sm ${!c.read ? 'text-foreground' : 'text-muted-foreground'}`}>{c.message}</p>
                    </div>
                  ))}
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* SETTINGS TAB */}
        <TabsContent value="settings">
          {settings ? (
            <div className="space-y-4">
              <Card>
                <CardHeader><CardTitle>Default Scoring Multipliers</CardTitle></CardHeader>
                <CardContent>
                  <table className="w-full text-sm" style={{ minWidth: "700px" }}>
                    <thead><tr className="border-b">
                      <th className="px-3 py-2 text-left text-xs font-semibold">Tier</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold">Win</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold">Draw</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold">Loss</th>
                    </tr></thead>
                    <tbody>
                      {[1,2,3,4,5].map(t => {
                        const tier = settings.default_tier_multipliers?.[String(t)] || {};
                        return (
                          <tr key={t} className="border-b">
                            <td className="px-3 py-2 font-semibold">Tier {t}</td>
                            <td className="px-3 py-2 text-center"><Input type="number" value={tier.win || 0} onChange={(e) => setSettings(s => ({...s, default_tier_multipliers: {...s.default_tier_multipliers, [String(t)]: {...s.default_tier_multipliers?.[String(t)], win: parseInt(e.target.value)||0}}}))} className="h-8 w-20 text-center mx-auto" /></td>
                            <td className="px-3 py-2 text-center"><Input type="number" value={tier.draw || 0} onChange={(e) => setSettings(s => ({...s, default_tier_multipliers: {...s.default_tier_multipliers, [String(t)]: {...s.default_tier_multipliers?.[String(t)], draw: parseInt(e.target.value)||0}}}))} className="h-8 w-20 text-center mx-auto" /></td>
                            <td className="px-3 py-2 text-center text-muted-foreground">0</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Default Prize Structure</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                    {['first','second','third','middle','goals'].map(k => (
                      <div key={k}><label className="text-xs font-medium capitalize">{k} %</label><Input type="number" value={settings.default_prize_config?.[k] || 0} onChange={(e) => setSettings(s => ({...s, default_prize_config: {...s.default_prize_config, [k]: parseInt(e.target.value)||0}}))} className="h-8" /></div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle>Other Settings</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div><label className="text-xs font-medium">Max Entries Per Person</label><Input type="number" value={settings.max_entries_per_person || 2} onChange={(e) => setSettings(s => ({...s, max_entries_per_person: parseInt(e.target.value)||2}))} className="h-8 w-24" /></div>
                  <div><label className="text-xs font-medium">API-Football Key</label><p className="text-sm text-muted-foreground font-mono">{settings.api_football_key || 'Not set'}</p></div>
                </CardContent>
              </Card>
              <Button onClick={saveSettings} disabled={savingSettings} className="bg-[#5b3cc4] hover:bg-[#4e33a8]">{savingSettings ? 'Saving...' : 'Save Settings'}</Button>
            </div>
          ) : <div className="h-32 bg-muted animate-pulse rounded-xl" />}
        </TabsContent>
      </Tabs>

      {/* User Detail Modal */}
      {selectedUser && userDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedUser(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-4 sm:p-6 mx-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold">User Detail</h2>
              <button onClick={() => setSelectedUser(null)} className="text-muted-foreground hover:text-foreground text-lg">✕</button>
            </div>
            <div className="space-y-3 mb-6">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Name:</span> <strong>{userDetail.user?.name}</strong></div>
                <div><span className="text-muted-foreground">Email:</span> <strong>{userDetail.user?.email}</strong></div>
                <div><span className="text-muted-foreground">Role:</span> <Badge variant="secondary">{userDetail.user?.role}</Badge></div>
                <div><span className="text-muted-foreground">Joined:</span> <strong>{userDetail.user?.created_at ? new Date(userDetail.user.created_at).toLocaleDateString() : '?'}</strong></div>
              </div>
            </div>
            {userDetail.pools_created?.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-sm mb-2">Pools Created ({userDetail.pools_created.length})</h3>
                {userDetail.pools_created.map(p => (
                  <div key={p.id} className="text-sm py-1 border-b flex justify-between"><span>{p.name}</span><Badge variant="secondary" className="text-[10px]">{p.plan_label || 'Free'}</Badge></div>
                ))}
              </div>
            )}
            {userDetail.pools_joined?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-2">Pools Joined ({userDetail.pools_joined.length})</h3>
                {userDetail.pools_joined.map(pj => (
                  <div key={pj.pool?.id} className="border rounded-lg p-3 mb-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">{pj.pool?.name}</span>
                      <Badge className={pj.fee_paid ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-red-100 text-red-700 text-[10px]'}>{pj.fee_paid ? 'Fee Paid' : 'Unpaid'}</Badge>
                    </div>
                    {pj.team_alias && <p className="text-xs text-[#5b3cc4]">{pj.team_alias}</p>}
                    {pj.entries?.map(e => (
                      <div key={e.id} className="text-xs text-muted-foreground mt-1">Entry {e.entry_number}: {e.total_points} pts | Rank #{e.rank} | {e.picks?.length || 0} picks</div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
